import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals `revokeMembership` answers with (PCG-357).
 *
 * A vocabulary of its own, the mirror of `reinstateRefusal`: the code names the gesture
 * that unblocks the situation, and the wording is ours, the API's own sentences being
 * notes written for a log. The reading of the code stays in `refusalCode`.
 *
 * Two codes are proper to closing and worth their care:
 *
 * **`LAST_ADMINISTRATOR` is not about this person.** Closing this access would leave the
 * account with nobody holding `manage_users`, so the platform refuses it: the fix is to
 * give another person that right on the account first, not to retry. It only fires for a
 * portal (client) actor; an internal operator is not stopped.
 *
 * **`CANNOT_REVOKE_SELF`** guards the operator from cutting their own access: it is met
 * only on their own sheet, and the way out is to have someone else do it.
 */
export const revokeRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'CANNOT_REVOKE_SELF':
            return {
                title: c('Revoke refusal').t`You cannot close your own access.`,
                guidance: c('Revoke refusal')
                    .t`Closing yourself would cut your own way in. Have another administrator do it if it must be done.`,
            };
        case 'ALREADY_REVOKED':
            return {
                title: c('Revoke refusal').t`This access is already closed.`,
                guidance: c('Revoke refusal')
                    .t`There is nothing to close: it was closed since this page was loaded. Reload the sheet to read its current state.`,
            };
        case 'LAST_ADMINISTRATOR':
            return {
                title: c('Revoke refusal').t`This account would be left with nobody to administer it.`,
                guidance: c('Revoke refusal')
                    .t`This person is the last one who can manage the users of this account. Give another person that right first, then close this access.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Revoke refusal').t`This access no longer exists.`,
                guidance: c('Revoke refusal')
                    .t`It has disappeared since this page was opened. Go back to the list: the sheet you are reading is stale.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Revoke refusal').t`You are not allowed to administer this person.`,
                guidance: c('Revoke refusal')
                    .t`Closing an access requires the right to manage the users of the account it belongs to, which your role does not carry. Ask for that right, or hand the gesture to whoever administers the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Revoke refusal').t`This account is outside your portfolio.`,
                guidance: c('Revoke refusal')
                    .t`The access belongs to an account that is not entrusted to you. Ask for that account to be added to your portfolio, or hand the gesture over.`,
            };
        default:
            return {
                title: c('Revoke refusal').t`The access was not closed.`,
                guidance: c('Revoke refusal')
                    .t`This refusal is not one the screen knows how to read. Nothing has changed: the access is still open exactly as it was. The server's answer is below, and it is what the support desk needs.`,
                // Kept only for an unknown code: without it the operator has nothing to report.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
